import * as THREE from 'three';
import {
  chamferBox,
  plainBox,
  tubeZ,
  rodZ,
  pipeZ,
  ringZ,
  picatinnyRail,
  slottedPanel,
  gripTexture,
  mergeGeom,
  place,
  translate,
  rotate,
} from './primitives.js';
import { materials } from './materials.js';

/**
 * The weapon parts library.
 *
 * Twenty-three weapons cannot be hand-built as twenty-three monoliths — the first six
 * were, and between them they carried three copies of the same bore-sleeve bug and two
 * copies of the same inside-out turret. Everything reusable lives here as a
 * parameterised builder, so a fix lands once and every weapon that uses the part gets
 * it.
 *
 * All part builders take `(K, o)`: `K` is a kit (material buckets plus a `parts` map)
 * and `o` is the part's options. They push geometry into the kit's buckets and return
 * whatever attachment data the caller needs (the top of a rail, the tip of a
 * magazine, the far end of a barrel).
 *
 * Model space is the same for every weapon:
 *   origin = a point on the bore axis directly above the trigger
 *   -Z     = downrange, +X = the shooter's right, +Y = up, units = real metres.
 */

/* -------------------------------------------------------------------------- */
/* kit                                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A build in progress: one geometry bucket per material, plus the `parts` map the
 * animation layer reads. Buckets are merged into a single mesh each at the end, so a
 * whole weapon is five or six draw calls regardless of how many components it has.
 */
export function kit(ctx) {
  const M = materials(ctx);
  return {
    ctx,
    M,
    parts: {},
    buckets: {
      metal: [],
      dark: [],
      blued: [],
      poly: [],
      tan: [],
      grey: [],
      wood: [],
      woodLight: [],
      rubber: [],
      brass: [],
      accent: [],
    },
    push(bucket, ...geoms) {
      const b = this.buckets[bucket];
      if (!b) throw new Error(`[weapons] no material bucket "${bucket}"`);
      for (const g of geoms) if (g) b.push(g);
      return this;
    },
    metal(...g) { return this.push('metal', ...g); },
    dark(...g) { return this.push('dark', ...g); },
    blued(...g) { return this.push('blued', ...g); },
    poly(...g) { return this.push('poly', ...g); },
    rubber(...g) { return this.push('rubber', ...g); },
  };
}

/** Merge every non-empty bucket into one mesh apiece and parent them to a group. */
export function assembleKit(K) {
  const root = new THREE.Group();
  for (const [name, geoms] of Object.entries(K.buckets)) {
    if (!geoms.length) continue;
    const m = new THREE.Mesh(mergeGeom(geoms), K.M[name]);
    m.frustumCulled = false;
    m.castShadow = false;
    m.receiveShadow = false;
    m.renderOrder = 2;
    m.name = name;
    root.add(m);
  }
  return root;
}

/* -------------------------------------------------------------------------- */
/* small geometry helpers                                                      */
/* -------------------------------------------------------------------------- */

/** Flat disc facing +Z (dir = 1) or -Z (dir = -1). */
export function discZ(r, z, seg = 14, dir = 1) {
  return dir > 0 ? tubeZ([[r, z], [0, z]], seg) : tubeZ([[0, z], [r, z]], seg);
}

/** Cylinder whose axis runs along X. */
export function rodX(r, x0, x1, seg = 10) {
  const g = rodZ(r, -(x1 - x0) / 2, (x1 - x0) / 2, seg);
  rotate(g, 0, Math.PI / 2, 0);
  return translate(g, (x0 + x1) / 2, 0, 0);
}

/** Cylinder whose axis runs along Y. */
export function rodY(r, y0, y1, seg = 10) {
  const g = rodZ(r, -(y1 - y0) / 2, (y1 - y0) / 2, seg);
  rotate(g, -Math.PI / 2, 0, 0);
  return translate(g, 0, (y0 + y1) / 2, 0);
}

/** Rounded trigger guard built from four short chamfered bars. */
export function triggerGuard(zFront, zBack, yTop, yBottom, width = 0.007) {
  const t = 0.006;
  const parts = [];
  const len = zBack - zFront;
  const drop = yTop - yBottom;
  parts.push(place(chamferBox(width, drop * 0.9, t, 0.0012), 0, yTop - drop * 0.45, zFront + 0.004, 0.30, 0, 0));
  parts.push(place(chamferBox(width, t, len * 0.72, 0.0012), 0, yBottom, zFront + len * 0.5));
  parts.push(place(chamferBox(width, drop * 0.7, t, 0.0012), 0, yTop - drop * 0.35, zBack - 0.003, -0.35, 0, 0));
  parts.push(place(chamferBox(width, t * 1.4, t * 2.2, 0.0012), 0, yBottom + 0.0015, zFront + len * 0.16));
  return mergeGeom(parts);
}

/** A row of pressed rivet heads — the detail that says "stamped sheet steel". */
export function rivetRow(count, from, to, r = 0.0022) {
  const parts = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0;
    const x = THREE.MathUtils.lerp(from[0], to[0], t);
    const y = THREE.MathUtils.lerp(from[1], to[1], t);
    const z = THREE.MathUtils.lerp(from[2], to[2], t);
    const g = rodZ(r, 0, 0.0022, 6);
    rotate(g, 0, Math.PI / 2, 0);
    parts.push(translate(g, x, y, z));
  }
  return mergeGeom(parts);
}

/** QD sling socket: a small ring standing off the left flank. */
export function slingLoop(x, y, z, major = 0.0062) {
  return place(ringZ(major, 0.0022, 12, 6), x, y, z, 0, Math.PI / 2, 0);
}

/* -------------------------------------------------------------------------- */
/* magazines                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A curved box magazine: stacked segments with progressive lean.
 *
 * `curvePerSeg` is the single number that separates an AK's banana from an AR's
 * near-straight STANAG, and it is the most recognisable thing about either weapon's
 * silhouette from the side. 0.03 rad/segment is a .338 box, 0.075 a 5.56 curve, 0.13
 * a 7.62x39 banana.
 */
export function curvedMagazine(w, d, segH, count, curvePerSeg, chamfer = 0.0022) {
  const parts = [];
  let y = 0;
  let z = 0;
  let ang = 0;
  for (let i = 0; i < count; i++) {
    ang += curvePerSeg;
    const g = chamferBox(w * (1 - i * 0.012), segH * 1.02, d, chamfer);
    rotate(g, ang, 0, 0);
    const dy = Math.cos(ang) * segH;
    const dz = -Math.sin(ang) * segH;
    translate(g, 0, y - dy / 2, z + dz / 2);
    y -= dy;
    z += dz;
    parts.push(g);
    if (i > 0) {
      parts.push(
        place(chamferBox(w * 0.28, 0.004, 0.004, 0.0008), 0, y + dy * 0.5, z - dz * 0.5 + d / 2, ang, 0, 0)
      );
    }
  }
  const g = chamferBox(w + 0.004, 0.010, d + 0.004, 0.0022);
  rotate(g, ang, 0, 0);
  translate(g, 0, y - 0.004, z + Math.sin(ang) * 0.004);
  parts.push(g);
  return { geom: mergeGeom(parts), tipY: y, tipZ: z };
}

/**
 * Straight box magazine — pistol-calibre sticks, .45 columns, 20-round battle-rifle
 * boxes. Ribbed down the flanks so it does not read as a plain slab.
 */
export function straightMagazine(w, d, len, chamfer = 0.0022, ribs = 3) {
  const parts = [];
  parts.push(place(chamferBox(w, len, d, chamfer), 0, -len / 2, 0));
  for (let i = 0; i < ribs; i++) {
    const y = -len * (0.22 + (i / Math.max(1, ribs - 1)) * 0.56);
    parts.push(place(chamferBox(w + 0.0016, 0.005, d * 0.6, 0.0008), 0, y, 0));
  }
  parts.push(place(chamferBox(w + 0.004, 0.009, d + 0.004, 0.0022), 0, -len - 0.004, 0));
  return { geom: mergeGeom(parts), tipY: -len, tipZ: 0 };
}

/**
 * Drum magazine: a neck into the magwell and a wide disc hanging under it, axis
 * lateral. The single most identifiable magazine shape there is — it is why the
 * Thompson and the RPK read instantly from a silhouette alone.
 */
export function drumMagazine(radius, width, neck = { w: 0.026, d: 0.034, h: 0.030 }) {
  const parts = [];
  parts.push(place(chamferBox(neck.w, neck.h, neck.d, 0.0020), 0, -neck.h / 2, 0));
  const cy = -neck.h - radius * 0.72;
  // Body: a short fat cylinder lying on its side.
  const body = rodZ(radius, -width / 2, width / 2, 16, 0.0030);
  rotate(body, 0, Math.PI / 2, 0);
  parts.push(translate(body, 0, cy, 0));
  // Rim band, then a ring of shallow ribs that reads as the spiral drive cover.
  const rim = tubeZ([[radius + 0.0022, -width / 2 + 0.004], [radius + 0.0022, width / 2 - 0.004]], 16);
  rotate(rim, 0, Math.PI / 2, 0);
  parts.push(translate(rim, 0, cy, 0));
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const rib = plainBox(width * 0.9, 0.004, 0.004);
    parts.push(translate(rib, 0, cy + Math.sin(a) * radius * 0.72, Math.cos(a) * radius * 0.72));
  }
  // Winding key on the right face.
  parts.push(place(chamferBox(0.012, 0.014, 0.014, 0.0014), width / 2 + 0.004, cy, 0));
  return { geom: mergeGeom(parts), tipY: cy - radius, tipZ: 0 };
}

/** Under-barrel tube magazine with an end cap, for pump and break-action shotguns. */
export function tubeMagazine(r, z0, z1, seg = 12) {
  return mergeGeom([
    rodZ(r, z0 + 0.010, z1, seg),
    tubeZ([[r, z0], [r + 0.0030, z0 + 0.008], [r + 0.0030, z0 + 0.016]], seg),
  ]);
}

/**
 * Belt of linked cartridges, following an arc from the box mouth into the feed tray.
 *
 * Cases are `plainBox`: a 10 mm cartridge spends its entire life under two pixels, so
 * a 44-triangle chamfered version would cost 500 triangles to render exactly the same
 * silhouette. `sag` bows the middle of the run downward, which is the detail that
 * stops a belt reading as a ladder — a real belt hangs.
 */
export function ammoBelt(count, from, to, sag = 0.012) {
  const parts = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0;
    const x = THREE.MathUtils.lerp(from[0], to[0], t);
    const y = THREE.MathUtils.lerp(from[1], to[1], t) - Math.sin(t * Math.PI) * sag;
    const z = THREE.MathUtils.lerp(from[2], to[2], t);
    const lean = (t - 0.5) * 0.5;
    parts.push(place(plainBox(0.0092, 0.030, 0.0112), x, y, z, lean, 0, 0));
    parts.push(place(plainBox(0.0104, 0.008, 0.0124), x, y + 0.013, z, lean, 0, 0));
    parts.push(place(plainBox(0.0060, 0.014, 0.0070), x, y - 0.020, z, lean, 0, 0));
  }
  return mergeGeom(parts);
}

/** Rectangular belt box (a SAW's nutsack), with stiffening ribs and a latch. */
export function beltBox(w, h, d) {
  const parts = [];
  parts.push(place(chamferBox(w, h, d, 0.0042), 0, -h / 2, 0));
  parts.push(place(chamferBox(w + 0.006, 0.014, d + 0.006, 0.0032), 0, 0.004, 0));
  parts.push(place(chamferBox(0.016, 0.022, 0.012, 0.0022), 0, -0.006, d / 2 + 0.005));
  parts.push(place(chamferBox(w * 0.8, 0.012, 0.014, 0.0022), 0, -0.008, -d / 2 - 0.003));
  for (let i = -1; i <= 1; i++) {
    parts.push(place(chamferBox(w + 0.004, 0.010, 0.010, 0.0018), 0, -h / 2, i * (d * 0.27)));
  }
  return mergeGeom(parts);
}

/**
 * Horizontal top-mounted magazine — the P90 layout, and the reason the Carbon 57 is
 * unmistakable. Rounds lie across the receiver under a translucent-looking shell, so
 * this is built as a long shallow trough with a witness rib down each side.
 */
export function horizontalMagazine(len, w, h) {
  const parts = [];
  parts.push(place(chamferBox(w, h, len, 0.0030), 0, 0, 0));
  parts.push(place(chamferBox(w * 0.55, h * 0.55, len * 0.98, 0.0020), 0, h * 0.42, 0));
  for (let i = 0; i < 7; i++) {
    const z = (i / 6 - 0.5) * len * 0.86;
    parts.push(place(plainBox(w + 0.0022, 0.0035, 0.0060), 0, -h * 0.10, z));
  }
  // Feed ramp at the rear, where the rounds turn 90 degrees down into the chamber.
  parts.push(place(chamferBox(w * 0.8, h * 1.1, 0.026, 0.0026), 0, -h * 0.20, len / 2 + 0.012));
  return mergeGeom(parts);
}

/* -------------------------------------------------------------------------- */
/* grips                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Pistol grip. `rake` is the lean back from vertical in radians — 0.26 is a precision
 * chassis (the hand only steers the trigger), 0.44 a service rifle, 0.62 an AK.
 */
export function pistolGrip(K, o = {}) {
  const w = o.w ?? 0.031;
  const h = o.h ?? 0.108;
  const d = o.d ?? 0.044;
  const bucket = o.bucket ?? 'poly';
  const g = [];
  g.push(place(chamferBox(w, h, d, 0.0055), 0, -h / 2, 0));
  g.push(place(chamferBox(w + 0.002, 0.014, d * 0.78, 0.0035), 0, -h - 0.006, 0.002));
  if (o.beavertail !== false) g.push(place(chamferBox(w + 0.003, 0.026, 0.014, 0.0030), 0, -0.010, -d * 0.32));
  const swells = o.swells ?? 3;
  for (let i = 0; i < swells; i++) {
    g.push(place(chamferBox(w - 0.002, 0.012, 0.008, 0.0025), 0, -0.030 - i * (h * 0.24), -d * 0.48));
  }
  g.push(translate(gripTexture(w * 0.78, h * 0.65, 3, 7, 0.0038, 0.0010), 0, -h * 0.51, d / 2 + 0.0002));
  const geo = mergeGeom(g);
  rotate(geo, -(o.rake ?? 0.44), 0, 0);
  translate(geo, 0, o.y ?? -0.060, o.z ?? 0.066);
  K.push(bucket, geo);
  return geo;
}

/**
 * A stock with no separate pistol grip — the wrist of a classic rifle or shotgun,
 * where the hand wraps the narrow part of the stock behind the receiver.
 */
export function stockWrist(K, o = {}) {
  const bucket = o.bucket ?? 'wood';
  const g = [];
  g.push(place(chamferBox(0.038, 0.064, 0.110, 0.0060), 0, -0.022, 0.052, -0.26, 0, 0));
  g.push(place(chamferBox(0.040, 0.052, 0.048, 0.0055), 0, -0.050, 0.018, -0.58, 0, 0));
  g.push(translate(gripTexture(0.024, 0.042, 3, 5, 0.0040, 0.0010), 0, -0.052, 0.040));
  const geo = mergeGeom(g);
  translate(geo, 0, o.y ?? -0.030, o.z ?? 0.086);
  K.push(bucket, geo);
  return geo;
}

/** Angled foregrip. `rake` leans it forward; 0.62 is an AFG, 0.0 a vertical grip. */
export function foregrip(K, o = {}) {
  const bucket = o.bucket ?? 'poly';
  const h = o.h ?? 0.062;
  const w = o.w ?? 0.026;
  const g = [];
  g.push(place(chamferBox(w, h, o.d ?? 0.030, 0.0045), 0, -h / 2, 0));
  g.push(place(chamferBox(w + 0.004, 0.010, (o.d ?? 0.030) + 0.006, 0.0030), 0, 0.002, 0));
  if (o.flare) g.push(place(chamferBox(w + 0.006, 0.010, (o.d ?? 0.030) + 0.008, 0.0030), 0, -h - 0.004, 0));
  g.push(translate(gripTexture(w * 0.74, h * 0.62, 3, 4, 0.0034, 0.0009), 0, -h * 0.52, (o.d ?? 0.030) / 2 + 0.0002));
  const geo = mergeGeom(g);
  rotate(geo, o.rake ?? 0.62, 0, 0);
  translate(geo, 0, o.y ?? -0.030, o.z ?? -0.252);
  K.push(bucket, geo);
  return geo;
}

/** Hand stop / barricade block: a low fence on the belly of a handguard. */
export function handStop(K, o = {}) {
  K.push(o.bucket ?? 'poly', place(chamferBox(o.w ?? 0.032, 0.014, 0.026, 0.0030), 0, o.y ?? -0.030, o.z ?? -0.200));
}

/* -------------------------------------------------------------------------- */
/* stocks                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Collapsible stock on a buffer tube — the AR-15 line, and the one stock in this
 * library whose silhouette is defined by the tube it slides on rather than by itself.
 */
export function stockCollapsible(K, o = {}) {
  const z0 = o.z0 ?? 0.096;
  const z1 = o.z1 ?? 0.250;
  const y = o.y ?? -0.004;
  K.metal(translate(rodZ(0.0168, z0, z1, 14), 0, y, 0));
  K.metal(translate(tubeZ([[0.021, z0 + 0.002], [0.021, z0 + 0.014], [0.0195, z0 + 0.017]], 12), 0, y, 0));
  const s = [];
  const bz = o.bodyZ ?? z1 - 0.054;
  s.push(place(chamferBox(0.042, 0.062, 0.104, 0.0050), 0, y - 0.006, bz));
  s.push(place(chamferBox(0.036, 0.020, 0.070, 0.0035), 0, y + 0.028, bz - 0.006));
  s.push(place(chamferBox(0.046, 0.088, 0.015, 0.0040), 0, y - 0.010, bz + 0.056));
  s.push(place(chamferBox(0.048, 0.014, 0.010, 0.0025), 0, y - 0.048, bz + 0.050));
  s.push(place(chamferBox(0.030, 0.024, 0.026, 0.0030), 0, y - 0.036, bz - 0.030));
  K.push(o.bucket ?? 'poly', mergeGeom(s));
  K.metal(slingLoop(-0.0215, y - 0.016, bz + 0.012));
  return { rearZ: bz + 0.064 };
}

/**
 * Fixed A2-style tube stock: one continuous taper from the receiver to the butt with
 * a flat comb. Reads as "classic carbine" against a collapsible's stepped profile.
 */
export function stockFixedTube(K, o = {}) {
  const z0 = o.z0 ?? 0.092;
  const len = o.len ?? 0.190;
  const y = o.y ?? -0.006;
  const s = [];
  s.push(place(chamferBox(0.042, 0.058, len, 0.0050), 0, y - 0.004, z0 + len / 2, -0.045, 0, 0));
  s.push(place(chamferBox(0.036, 0.022, len * 0.82, 0.0035), 0, y + 0.026, z0 + len * 0.46, -0.045, 0, 0));
  s.push(place(chamferBox(0.048, 0.092, 0.018, 0.0045), 0, y - 0.014, z0 + len + 0.006, -0.045, 0, 0));
  s.push(place(chamferBox(0.032, 0.026, 0.030, 0.0035), 0, y - 0.028, z0 + 0.014));
  K.push(o.bucket ?? 'poly', mergeGeom(s));
  K.rubber(place(chamferBox(0.049, 0.090, 0.012, 0.0035), 0, y - 0.014, z0 + len + 0.016, -0.045, 0, 0));
  K.metal(slingLoop(0, y - 0.048, z0 + len - 0.006));
  return { rearZ: z0 + len + 0.022 };
}

/**
 * Skeleton stock: two struts and a comb with the lightening cut left genuinely open.
 * Its whole silhouette is the hole in the middle, so the struts stay thin.
 */
export function stockSkeleton(K, o = {}) {
  const z0 = o.z0 ?? 0.110;
  const len = o.len ?? 0.190;
  const y = o.y ?? 0;
  const s = [];
  s.push(place(chamferBox(0.036, 0.026, len, 0.0030), 0, y + 0.016, z0 + len / 2));
  s.push(place(chamferBox(0.032, 0.022, len * 0.88, 0.0030), 0, y - 0.060, z0 + len * 0.46));
  s.push(place(chamferBox(0.040, 0.070, 0.026, 0.0035), 0, y - 0.022, z0 + 0.012));
  s.push(place(chamferBox(0.048, 0.104, 0.022, 0.0045), 0, y - 0.020, z0 + len + 0.006));
  s.push(place(chamferBox(0.050, 0.016, 0.016, 0.0030), 0, y - 0.068, z0 + len - 0.004));
  K.push(o.bucket ?? 'poly', mergeGeom(s));
  if (o.cheek !== false) {
    const c = [];
    c.push(place(chamferBox(0.042, 0.024, len * 0.62, 0.0035), 0, y + 0.046, z0 + len * 0.50));
    c.push(place(chamferBox(0.010, 0.028, 0.010, 0.0014), -0.014, y + 0.032, z0 + len * 0.20));
    c.push(place(chamferBox(0.010, 0.028, 0.010, 0.0014), 0.014, y + 0.032, z0 + len * 0.20));
    K.push(o.bucket ?? 'poly', mergeGeom(c));
    K.metal(place(chamferBox(0.012, 0.014, 0.012, 0.0012), 0.024, y + 0.032, z0 + len * 0.20));
  }
  K.metal(slingLoop(-0.0215, y - 0.062, z0 + len * 0.66));
  return { rearZ: z0 + len + 0.020 };
}

/**
 * Side-folding skeleton stock, folded *out*. Built as a frame hinged off the left of
 * the receiver: two rails and a pad, with the hinge boss visible. The asymmetric
 * hinge is the tell — a fixed stock has nothing sticking out on one side.
 */
export function stockFolding(K, o = {}) {
  const z0 = o.z0 ?? 0.100;
  const len = o.len ?? 0.180;
  const y = o.y ?? -0.006;
  K.metal(place(chamferBox(0.030, 0.046, 0.030, 0.0028), -0.010, y, z0 + 0.010)); // hinge boss
  K.metal(translate(rodX(0.0058, -0.028, 0.010, 8), 0, y, z0 + 0.010));
  const s = [];
  for (const sy of [1, -1]) {
    s.push(place(chamferBox(0.014, 0.012, len, 0.0022), 0, y + sy * 0.024, z0 + len / 2 + 0.012));
  }
  s.push(place(chamferBox(0.016, 0.062, 0.016, 0.0022), 0, y, z0 + len + 0.014)); // rear web
  K.push(o.bucket ?? 'poly', mergeGeom(s));
  K.push(o.bucket ?? 'poly', place(chamferBox(0.040, 0.080, 0.020, 0.0040), 0, y - 0.004, z0 + len + 0.030));
  K.rubber(place(chamferBox(0.041, 0.078, 0.012, 0.0030), 0, y - 0.004, z0 + len + 0.042));
  if (o.cheek) K.push(o.bucket ?? 'poly', place(chamferBox(0.030, 0.020, len * 0.6, 0.0030), 0, y + 0.036, z0 + len * 0.52));
  return { rearZ: z0 + len + 0.048 };
}

/**
 * Retracting wire stock: two rods and a shoulder plate, the MP5A3 / Uzi answer. It is
 * the lightest silhouette in the library and reads as a skeleton against the sky.
 */
export function stockWire(K, o = {}) {
  const z0 = o.z0 ?? 0.054;
  const z1 = o.z1 ?? 0.196;
  const y = o.y ?? -0.010;
  const dx = o.dx ?? 0.019;
  K.metal(translate(rodZ(0.0072, z0, z1, 8), -dx, y, 0));
  K.metal(translate(rodZ(0.0072, z0, z1, 8), dx, y, 0));
  K.push(o.bucket ?? 'poly', place(chamferBox(0.052, 0.062, 0.016, 0.0040), 0, y - 0.002, z1 + 0.004));
  K.push(o.bucket ?? 'poly', place(chamferBox(0.044, 0.030, 0.030, 0.0035), 0, y, z1 - 0.012));
  K.metal(slingLoop(-dx, y - 0.016, z1 - 0.020, 0.0060));
  return { rearZ: z1 + 0.014 };
}

/**
 * One-piece wooden rifle stock: a straight comb running from the receiver to the butt
 * with the wrist swelling under it. No pistol grip — that absence *is* the silhouette,
 * and it is what separates a classic battle rifle from everything else in the roster.
 */
export function stockWood(K, o = {}) {
  const bucket = o.bucket ?? 'wood';
  const z0 = o.z0 ?? 0.080;
  const len = o.len ?? 0.250;
  const y = o.y ?? -0.020;
  const drop = o.drop ?? 0.13;
  /**
   * Segments overlap by design.
   *
   * Each piece is rotated about its own centre by a different amount to give the stock
   * its drop, and two boxes that merely *abut* end-to-end come apart the moment their
   * rotations differ — which is what turned three of these into a fan of loose slabs.
   * Every segment therefore runs long and buries its ends inside its neighbours, so
   * the drop can be whatever the weapon wants without opening a seam.
   */
  const s = [];
  s.push(place(chamferBox(0.040, 0.066, len * 0.66, 0.0060), 0, y - 0.010, z0 + len * 0.30, -drop * 0.5, 0, 0));
  s.push(place(chamferBox(0.044, 0.084, len * 0.62, 0.0060), 0, y - 0.034, z0 + len * 0.70, -drop, 0, 0));
  s.push(place(chamferBox(0.046, 0.040, len * 0.54, 0.0050), 0, y + 0.014, z0 + len * 0.32, -drop * 0.4, 0, 0)); // comb
  s.push(place(chamferBox(0.048, 0.104, 0.020, 0.0050), 0, y - 0.052, z0 + len + 0.006, -drop, 0, 0));
  K.push(bucket, mergeGeom(s));
  K.rubber(place(chamferBox(0.049, 0.102, 0.013, 0.0035), 0, y - 0.054, z0 + len + 0.017, -drop, 0, 0));
  K.metal(slingLoop(0, y - 0.070, z0 + len * 0.72, 0.0068));
  return { rearZ: z0 + len + 0.026 };
}

/**
 * AK-pattern fixed stock: a downward-raked wedge whose *toe* line is what the eye
 * reads. An AK's stock drops away from the bore far more sharply than an AR's, and
 * getting that rake wrong is the difference between "AK" and "generic rifle".
 */
export function stockAK(K, o = {}) {
  const bucket = o.bucket ?? 'wood';
  const z0 = o.z0 ?? 0.078;
  const len = o.len ?? 0.208;
  const rake = o.rake ?? 0.135;
  const s = [];
  s.push(place(chamferBox(0.038, 0.072, len, 0.0055), 0, -0.024, z0 + len / 2, -rake, 0, 0));
  s.push(place(chamferBox(0.040, 0.028, len * 0.86, 0.0040), 0, 0.008, z0 + len * 0.44, -rake, 0, 0));
  // The scallop cut on the underside behind the grip — an AK's most-copied detail.
  s.push(place(chamferBox(0.030, 0.026, 0.052, 0.0035), 0, -0.058, z0 + 0.044, -rake, 0, 0));
  s.push(place(chamferBox(0.044, 0.094, 0.018, 0.0045), 0, -0.044, z0 + len + 0.004, -rake, 0, 0));
  K.push(bucket, mergeGeom(s));
  K.metal(place(chamferBox(0.045, 0.092, 0.008, 0.0025), 0, -0.048, z0 + len + 0.015, -rake, 0, 0));
  K.metal(slingLoop(-0.0205, -0.052, z0 + 0.030, 0.0064));
  return { rearZ: z0 + len + 0.024 };
}

/**
 * Thumbhole skeleton stock (SVD pattern): a comb bar over an open hole, with the
 * grip formed by the front web. The hole is the silhouette.
 */
export function stockThumbhole(K, o = {}) {
  const bucket = o.bucket ?? 'wood';
  const z0 = o.z0 ?? 0.070;
  const len = o.len ?? 0.230;
  const s = [];
  s.push(place(chamferBox(0.036, 0.030, len, 0.0045), 0, 0.010, z0 + len / 2, -0.10, 0, 0)); // comb bar
  s.push(place(chamferBox(0.034, 0.026, len * 0.70, 0.0040), 0, -0.080, z0 + len * 0.56, -0.06, 0, 0)); // toe bar
  s.push(place(chamferBox(0.036, 0.086, 0.030, 0.0045), 0, -0.036, z0 + 0.020, -0.30, 0, 0)); // grip web
  s.push(place(chamferBox(0.036, 0.040, 0.026, 0.0040), 0, -0.062, z0 + len * 0.72)); // rear web
  s.push(place(chamferBox(0.044, 0.098, 0.020, 0.0045), 0, -0.032, z0 + len + 0.006, -0.10, 0, 0));
  K.push(bucket, mergeGeom(s));
  K.rubber(place(chamferBox(0.045, 0.096, 0.012, 0.0032), 0, -0.032, z0 + len + 0.017, -0.10, 0, 0));
  K.push(bucket, place(chamferBox(0.040, 0.022, len * 0.44, 0.0035), 0, 0.036, z0 + len * 0.50, -0.10, 0, 0)); // cheek
  return { rearZ: z0 + len + 0.026 };
}

/**
 * Bullpup butt: the receiver *is* the stock, so all that is needed is a pad, a
 * shoulder hook and a cheek shelf. Building anything more here is what makes a
 * bullpup look like a conventional rifle with the magazine in the wrong place.
 */
export function stockBullpup(K, o = {}) {
  const bucket = o.bucket ?? 'poly';
  const z = o.z ?? 0.290;
  const y = o.y ?? -0.010;
  const s = [];
  s.push(place(chamferBox(0.048, 0.106, 0.024, 0.0045), 0, y, z));
  s.push(place(chamferBox(0.050, 0.018, 0.030, 0.0030), 0, y - 0.052, z - 0.012)); // toe hook
  s.push(place(chamferBox(0.038, 0.020, 0.060, 0.0030), 0, y + 0.048, z - 0.038)); // cheek shelf
  K.push(bucket, mergeGeom(s));
  K.rubber(place(chamferBox(0.049, 0.104, 0.013, 0.0035), 0, y, z + 0.017));
  K.metal(slingLoop(-0.0225, y - 0.030, z - 0.030, 0.0064));
  return { rearZ: z + 0.024 };
}

/* -------------------------------------------------------------------------- */
/* handguards                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Octagonal M-LOK handguard: two slotted side panels, a slotted belly and four
 * 45-degree fillets. The gaps are real geometry, so the silhouette breaks up the way
 * a modern rail does rather than relying on a texture.
 */
export function hgMlok(K, o = {}) {
  const len = o.len ?? 0.198;
  const z = o.z ?? -0.231;
  const r = o.r ?? 0.0212;
  const slots = o.slots ?? 5;
  const bucket = o.bucket ?? 'metal';
  const t = o.t ?? 0.0055;
  const panelW = o.panelW ?? r * 1.70;

  const side = slottedPanel(panelW, t, len, slots, 0.44);
  const left = rotate(side.clone(), 0, 0, Math.PI / 2);
  K.push(bucket, translate(left, -r, o.y ?? 0.001, z));
  const right = rotate(side, 0, 0, Math.PI / 2);
  K.push(bucket, translate(right, r, o.y ?? 0.001, z));

  const belly = slottedPanel(panelW * 0.89, t, len, slots, 0.44);
  K.push(bucket, translate(belly, 0, (o.y ?? 0.001) - r, z));

  for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
    const f = chamferBox(r * 0.90, t, len, 0.0010);
    rotate(f, 0, 0, (sx * sy > 0 ? -1 : 1) * Math.PI * 0.25);
    translate(f, sx * r * 0.783, sy * r * 0.783 + (o.y ?? 0.001), z);
    K.push(bucket, f);
  }
  if (o.endCap !== false) {
    K.push(bucket, translate(tubeZ([[r + 0.003, z - len / 2 - 0.006], [r + 0.003, z - len / 2], [r - 0.001, z - len / 2 + 0.002]], 14), 0, o.y ?? 0.001, 0));
  }
  return { frontZ: z - len / 2, backZ: z + len / 2, r };
}

/**
 * Quad-rail handguard: four Picatinny faces on a square body. Heavier and blockier
 * than M-LOK, which is exactly the point — it is the older, more military look.
 */
export function hgQuad(K, o = {}) {
  const len = o.len ?? 0.180;
  const z = o.z ?? -0.220;
  const r = o.r ?? 0.0224;
  const bucket = o.bucket ?? 'metal';
  K.push(bucket, place(chamferBox(r * 1.85, r * 1.85, len, 0.0014), 0, o.y ?? 0, z));
  K.push(bucket, translate(picatinnyRail(len - 0.010, 0.0196, z + len / 2 - 0.005), 0, (o.y ?? 0) + r * 0.92, 0));
  K.push(bucket, translate(rotate(picatinnyRail(len - 0.010, 0.0196, z + len / 2 - 0.005), 0, 0, Math.PI), 0, (o.y ?? 0) - r * 0.92, 0));
  for (const sx of [-1, 1]) {
    K.push(bucket, translate(rotate(picatinnyRail(len - 0.010, 0.0196, z + len / 2 - 0.005), 0, 0, sx * Math.PI / 2), sx * r * 0.92, o.y ?? 0, 0));
  }
  return { frontZ: z - len / 2, backZ: z + len / 2, r: r * 1.28 };
}

/**
 * Ribbed polymer handguard, AK style: a lower cup with finger swells and a separate
 * upper heat guard with vents, split by the gas tube. The *gap* between the two halves
 * is the recognisable part.
 */
export function hgRibbed(K, o = {}) {
  const len = o.len ?? 0.150;
  const z = o.z ?? -0.230;
  const bucket = o.bucket ?? 'wood';
  const lower = [];
  lower.push(place(chamferBox(0.046, 0.034, len, 0.0050), 0, -0.020, z));
  for (let i = 0; i < 4; i++) {
    const zz = z - len / 2 + len * (0.18 + i * 0.22);
    lower.push(place(chamferBox(0.050, 0.012, 0.020, 0.0028), 0, -0.026, zz));
  }
  lower.push(place(chamferBox(0.050, 0.026, 0.014, 0.0035), 0, -0.020, z + len / 2 + 0.005));
  K.push(bucket, mergeGeom(lower));

  /**
   * Upper heat guard.
   *
   * Its top is the tightest sight-line clearance on every weapon that wears this
   * handguard. At 38 mm it clipped the AK-27's sight picture to 3.0 degrees — measured
   * as a hit on the wood at model y = 0.0380, 26 cm down the barrel. The optic on an
   * AK sits high precisely because the gas system does; the handguard has to stay
   * under the cone, not fight it.
   */
  const upY = o.upperY ?? 0.015;
  const upH = o.upperH ?? 0.018;
  const upper = [];
  upper.push(place(chamferBox(0.040, upH, len * 0.86, 0.0045), 0, upY, z - 0.004));
  for (let i = 0; i < 3; i++) {
    const zz = z - len * 0.3 + i * len * 0.26;
    upper.push(place(chamferBox(0.044, 0.008, 0.016, 0.0025), 0, upY + upH * 0.30, zz));
  }
  K.push(bucket, mergeGeom(upper));
  // Gas tube peeking out between the two halves.
  K.metal(translate(rodZ(0.0060, z - len / 2 - 0.010, z + len / 2 + 0.020, 10), 0, o.tubeY ?? 0.0032, 0));
  return { frontZ: z - len / 2, backZ: z + len / 2, r: 0.024 };
}

/** Two-piece wooden forend with a barrel band — the classic rifle look. */
export function hgWood(K, o = {}) {
  const len = o.len ?? 0.230;
  const z = o.z ?? -0.240;
  const bucket = o.bucket ?? 'wood';
  const s = [];
  s.push(place(chamferBox(0.046, 0.040, len, 0.0060), 0, -0.020, z));
  s.push(place(chamferBox(0.040, 0.024, len * 0.94, 0.0050), 0, 0.014, z)); // upper handguard
  s.push(place(chamferBox(0.050, 0.048, 0.016, 0.0050), 0, -0.016, z + len / 2 - 0.004));
  K.push(bucket, mergeGeom(s));
  // Steel barrel bands: the two bright rings that break up a long wooden forend.
  for (const bz of [z - len * 0.34, z + len * 0.30]) {
    K.metal(place(chamferBox(0.050, 0.046, 0.014, 0.0018), 0, -0.004, bz));
  }
  K.metal(slingLoop(0, -0.042, z - len * 0.20, 0.0070));
  return { frontZ: z - len / 2, backZ: z + len / 2, r: 0.025 };
}

/**
 * Faceted hexagonal handguard with lightening cut-outs: the "future rifle" look. Built
 * as six flats rather than a tube so it catches six separate highlights.
 */
export function hgHex(K, o = {}) {
  const len = o.len ?? 0.190;
  const z = o.z ?? -0.230;
  const r = o.r ?? 0.026;
  const bucket = o.bucket ?? 'grey';
  const shell = tubeZ([[r, z - len / 2], [r, z + len / 2]], 6);
  K.push(bucket, translate(shell, 0, o.y ?? 0, 0));
  // Cap both ends so the shell does not read as an open pipe from the front.
  K.push(bucket, translate(tubeZ([[r, z - len / 2], [r * 0.72, z - len / 2 - 0.008]], 6), 0, o.y ?? 0, 0));
  // Lightening windows: pairs of angled bars across each upper flat.
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      const zz = z - len * 0.30 + i * len * 0.30;
      K.push(bucket, place(chamferBox(0.006, 0.020, 0.030, 0.0012), sx * r * 0.90, (o.y ?? 0) + 0.006, zz, 0, 0, sx * 0.5));
    }
  }
  K.metal(translate(picatinnyRail(len * 0.34, 0.0180, z - len * 0.10), 0, (o.y ?? 0) - r * 0.94, 0));
  return { frontZ: z - len / 2, backZ: z + len / 2, r };
}

/**
 * Slotted barrel shroud / heat shield. One of the most recognisable shotgun and
 * subgun silhouettes there is: a cage of ribs standing off the barrel with real air
 * between them.
 */
export function hgShroud(K, o = {}) {
  const z0 = o.z0 ?? -0.400;
  const z1 = o.z1 ?? -0.140;
  const r = o.r ?? 0.0165;
  const rings = o.rings ?? 6;
  const bucket = o.bucket ?? 'metal';
  const g = [];
  for (let i = 0; i < rings; i++) {
    const z = z1 - (i + 0.5) * ((z1 - z0) / rings);
    g.push(place(chamferBox(r * 2.06, 0.010, 0.020, 0.0012), 0, r * 0.66, z));
    g.push(place(chamferBox(0.010, r * 2.06, 0.020, 0.0012), r * 0.90, 0.0, z));
    g.push(place(chamferBox(0.010, r * 2.06, 0.020, 0.0012), -r * 0.90, 0.0, z));
  }
  const spineLen = z1 - z0;
  g.push(place(chamferBox(0.008, 0.008, spineLen, 0.0010), r * 0.76, r * 0.56, (z0 + z1) / 2));
  g.push(place(chamferBox(0.008, 0.008, spineLen, 0.0010), -r * 0.76, r * 0.56, (z0 + z1) / 2));
  K.push(bucket, mergeGeom(g));
  return { frontZ: z0, backZ: z1, r: r * 1.25 };
}

/**
 * Slim tubular polymer handguard with vent slots — the SMG answer, where there is no
 * room for a rail system and the hand wraps the tube directly.
 */
export function hgTube(K, o = {}) {
  const len = o.len ?? 0.150;
  const z = o.z ?? -0.196;
  const bucket = o.bucket ?? 'poly';
  const h = [];
  h.push(place(chamferBox(o.w ?? 0.038, o.h ?? 0.036, len, 0.0050), 0, o.y ?? -0.006, z));
  const vents = o.vents ?? 4;
  for (let i = 0; i < vents; i++) {
    const zz = z - len / 2 + len * ((i + 0.6) / vents);
    h.push(place(chamferBox((o.w ?? 0.038) + 0.004, 0.010, 0.016, 0.0018), 0, (o.y ?? -0.006) + 0.012, zz));
  }
  h.push(place(chamferBox((o.w ?? 0.038) - 0.004, 0.014, 0.030, 0.0035), 0, (o.y ?? -0.006) - 0.018, z + len / 2 - 0.020));
  K.push(bucket, mergeGeom(h));
  return { frontZ: z - len / 2, backZ: z + len / 2, r: (o.w ?? 0.038) / 2 };
}

/* -------------------------------------------------------------------------- */
/* barrels, gas systems, muzzle devices                                        */
/* -------------------------------------------------------------------------- */

/**
 * Barrel run. `steps` is a list of [radius, zFront, zBack] so a profile can go from a
 * heavy chamber to a pencil muzzle in as many stages as the weapon needs, and `flutes`
 * cuts the shallow channels that read as "match barrel" at a glance.
 */
export function barrel(K, o = {}) {
  const bucket = o.bucket ?? 'metal';
  for (const [r, zf, zb] of o.steps) K.push(bucket, translate(rodZ(r, zf, zb, o.seg ?? 12), 0, o.y ?? 0, 0));
  if (o.flutes) {
    const { count = 6, r, z, len, size = 0.005 } = o.flutes;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const f = plainBox(size, size, len);
      K.push(bucket, translate(f, Math.cos(a) * r, Math.sin(a) * r + (o.y ?? 0), z));
    }
  }
  if (o.fins) {
    // Cooling fins: a stack of discs around the chamber end. The retro SMG and the
    // belt-fed both live on this detail.
    const { count = 8, r, z0, z1, thickness = 0.005 } = o.fins;
    for (let i = 0; i < count; i++) {
      const z = THREE.MathUtils.lerp(z0, z1, count > 1 ? i / (count - 1) : 0);
      K.push(bucket, translate(rodZ(r, z - thickness / 2, z + thickness / 2, 12), 0, o.y ?? 0, 0));
    }
  }
  return { muzzleZ: Math.min(...o.steps.map((s) => s[1])) };
}

/** Gas block plus gas tube. `blockTop` is what has to stay under the sight cone. */
export function gasSystem(K, o = {}) {
  const bucket = o.bucket ?? 'metal';
  const bz = o.z ?? -0.338;
  K.push(bucket, place(chamferBox(o.w ?? 0.024, o.h ?? 0.026, o.d ?? 0.030, 0.0018), 0, o.y ?? 0.003, bz));
  if (o.tube !== false) {
    K.push(bucket, translate(rodZ(o.tubeR ?? 0.0034, bz + 0.002, o.tubeBack ?? -0.150, 8), 0, o.tubeY ?? 0.0125, 0));
  }
  if (o.regulator) K.push(bucket, place(chamferBox(0.014, 0.020, 0.014, 0.0012), 0.019, 0.004, bz));
  return { top: (o.y ?? 0.003) + (o.h ?? 0.026) / 2 };
}

/** A2 birdcage: a base ring plus prongs, i.e. real slots. */
export function muzzleBirdcage(K, o = {}) {
  const z = o.z ?? -0.442;
  const r = o.r ?? 0.0126;
  const len = o.len ?? 0.028;
  K.metal(pipeZ(r, r * 0.67, z, z + 0.024, 12));
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    K.metal(translate(chamferBox(r * 0.60, r * 0.60, len, 0.0011), Math.cos(a) * r * 0.83, Math.sin(a) * r * 0.83, z - len / 2 + 0.004));
  }
  K.metal(pipeZ(r, r * 0.71, z - len - 0.002, z - len + 0.006, 12));
  return { tipZ: z - len - 0.002 };
}

/** Ported muzzle brake: a heavy block with real side ports blown through it. */
export function muzzleBrake(K, o = {}) {
  const z = o.z ?? -0.520;
  const r = o.r ?? 0.0155;
  const len = o.len ?? 0.070;
  const ports = o.ports ?? 3;
  K.metal(pipeZ(r, o.bore ?? 0.0092, z - len, z, 14));
  for (let i = 0; i < ports; i++) {
    for (const sx of [-1, 1]) {
      K.metal(translate(plainBox(0.006, r * 1.16, 0.010), sx * r * 0.83, 0, z - 0.012 - i * (len / (ports + 0.6))));
    }
  }
  if (o.topPorts) {
    for (let i = 0; i < ports; i++) {
      K.metal(translate(plainBox(r * 1.10, 0.006, 0.009), 0, r * 0.82, z - 0.014 - i * (len / (ports + 0.6))));
    }
  }
  return { tipZ: z - len };
}

/**
 * Slant brake: a single angled cut across the muzzle. Tiny, and yet it is the second
 * thing anyone names about an AK after the magazine.
 */
export function muzzleSlant(K, o = {}) {
  const z = o.z ?? -0.410;
  const r = o.r ?? 0.0128;
  K.metal(pipeZ(r, r * 0.62, z - 0.036, z, 12));
  // The slant itself: a wedge that closes the top-left of the exit.
  K.metal(place(chamferBox(r * 1.5, r * 0.9, 0.020, 0.0012), 0, r * 0.55, z - 0.030, 0.42, 0, 0));
  K.metal(place(chamferBox(0.008, 0.008, 0.010, 0.0010), 0, -r * 0.70, z - 0.008)); // retaining detent
  return { tipZ: z - 0.038 };
}

/**
 * Suppressor: a long fat tube over the muzzle. It is the biggest single change a
 * silhouette can take, which is why only two weapons in the roster wear one.
 */
export function muzzleSuppressor(K, o = {}) {
  const z = o.z ?? -0.520;
  const r = o.r ?? 0.0210;
  const len = o.len ?? 0.180;
  K.blued(tubeZ([
    [r * 0.62, z], [r, z - 0.012], [r, z - len + 0.012], [r * 0.78, z - len],
  ], 16));
  K.blued(tubeZ([[r * 0.78, z - len], [r * 0.40, z - len]], 16)); // end face
  for (let i = 0; i < 5; i++) {
    const zz = z - 0.030 - i * ((len - 0.055) / 4);
    K.blued(translate(tubeZ([[r + 0.0018, zz - 0.004], [r + 0.0018, zz + 0.004]], 16), 0, 0, 0));
  }
  K.metal(pipeZ(r * 0.70, r * 0.55, z + 0.006, z + 0.020, 12)); // mount collar
  return { tipZ: z - len };
}

/** Breacher standoff: a toothed crown for putting a door hinge through a wall. */
export function muzzleBreacher(K, o = {}) {
  const z = o.z ?? -0.400;
  const r = o.r ?? 0.0180;
  K.metal(pipeZ(r, r * 0.68, z - 0.030, z, 12));
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    K.metal(translate(chamferBox(0.008, 0.008, 0.022, 0.0010), Math.cos(a) * r * 0.82, Math.sin(a) * r * 0.82, z - 0.040));
  }
  return { tipZ: z - 0.052 };
}

/** Cutts-style compensator: a fat drum with slots across the top. Retro SMG only. */
export function muzzleCompensator(K, o = {}) {
  const z = o.z ?? -0.330;
  const r = o.r ?? 0.0150;
  K.metal(pipeZ(r, r * 0.52, z - 0.042, z, 14));
  for (let i = 0; i < 4; i++) {
    K.metal(translate(plainBox(r * 1.30, 0.006, 0.007), 0, r * 0.80, z - 0.010 - i * 0.009));
  }
  K.metal(pipeZ(r * 1.14, r * 0.90, z, z + 0.008, 14));
  return { tipZ: z - 0.042 };
}

/** Blast can / flash can: a short cone that throws the flash forward. PDW look. */
export function muzzleFlashCan(K, o = {}) {
  const z = o.z ?? -0.300;
  const r = o.r ?? 0.0142;
  K.metal(tubeZ([[r * 0.62, z + 0.010], [r * 0.62, z - 0.006], [r, z - 0.030], [r, z - 0.038]], 14));
  K.metal(tubeZ([[r, z - 0.038], [r * 0.52, z - 0.038]], 14));
  return { tipZ: z - 0.038 };
}

/** MP5-style tri-lug adapter: a knurled collar with three lugs. */
export function muzzleTriLug(K, o = {}) {
  const z = o.z ?? -0.290;
  const r = o.r ?? 0.0105;
  K.metal(translate(rodZ(r, z, z + 0.022, 12), 0, 0, 0));
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    K.metal(translate(chamferBox(0.0065, 0.0065, 0.016, 0.0010), Math.cos(a) * r * 0.88, Math.sin(a) * r * 0.88, z + 0.008));
  }
  K.metal(pipeZ(r * 0.94, 0.0060, z - 0.020, z, 12));
  return { tipZ: z - 0.020 };
}

/**
 * Coil / accelerator muzzle for the roster's one energy weapon: a stack of rings on
 * standoff rails with an emissive winding between them. Deliberately the only muzzle
 * in the library that emits light.
 */
export function muzzleIon(K, o = {}) {
  const z = o.z ?? -0.520;
  const r = o.r ?? 0.0200;
  const len = o.len ?? 0.110;
  for (let i = 0; i < 4; i++) {
    const zz = z - 0.010 - i * (len / 4);
    K.metal(pipeZ(r - i * 0.0012, r * 0.46, zz - 0.008, zz + 0.008, 14));
    K.push('accent', translate(tubeZ([[r * 0.62, zz - 0.014], [r * 0.62, zz - 0.010]], 12), 0, 0, 0));
  }
  for (const sx of [-1, 1]) {
    K.metal(translate(chamferBox(0.008, 0.010, len + 0.020, 0.0012), sx * r * 0.80, 0, z - len / 2));
  }
  K.metal(pipeZ(r * 0.70, r * 0.44, z - len - 0.012, z - len, 14));
  return { tipZ: z - len - 0.012 };
}

/** Plain threaded muzzle with a protector — the "nothing fitted" option. */
export function muzzleThread(K, o = {}) {
  const z = o.z ?? -0.400;
  const r = o.r ?? 0.0098;
  K.metal(pipeZ(r, r * 0.62, z - 0.018, z, 12));
  for (let i = 0; i < 4; i++) {
    K.metal(translate(tubeZ([[r * 1.04, z - 0.016 + i * 0.004], [r * 1.04, z - 0.014 + i * 0.004]], 12), 0, 0, 0));
  }
  return { tipZ: z - 0.018 };
}

/* -------------------------------------------------------------------------- */
/* accessories                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Deployed bipod.
 *
 * The legs are extruded along **Y**, not Z, and that is the whole fix.
 *
 * The shipped version built each leg as `chamferBox(0.012, 0.012, len)` — long axis Z
 * — and then rotated it by 0.34 radians about X "to rake it down". But a 0.34 rotation
 * of a Z-extruded box leaves it 19 degrees off *horizontal*, so a 185 mm leg dropped
 * only 60 mm and pointed backwards down the barrel. Its top end finished 44 mm clear
 * of the yoke and the bipod rendered as two loose bars lying beside the rifle. Nobody
 * noticed because a folded bipod is mostly hidden under a handguard — until this
 * roster put a deployed one on a bullpup where it is the lowest thing on the weapon.
 *
 * Built along Y, the leg's top end is placed *at* the yoke by construction: the
 * direction of its own +Y axis after the Euler rotation is computed and the centre is
 * offset back along it by half its length. `rake` tips the pair backwards, `splay`
 * spreads the feet, and neither can pull the legs off the mount.
 */
export function bipod(K, o = {}) {
  const z = o.z ?? -0.372;
  const len = o.len ?? 0.200;
  const rake = o.rake ?? 0.26;
  const splay = o.splay ?? 0.22;
  const yTop = o.yTop ?? -0.040;
  const b = [];
  b.push(place(chamferBox(0.032, 0.024, 0.044, 0.0020), 0, yTop + 0.010, z));
  b.push(translate(rodX(0.0050, -0.022, 0.022, 8), 0, yTop, z));

  for (const sx of [-1, 1]) {
    const rz = sx * splay;
    // Direction of the leg's own +Y axis under Euler XYZ (rake, 0, rz).
    const ux = -Math.sin(rz);
    const uy = Math.cos(rz) * Math.cos(rake);
    const uz = Math.cos(rz) * Math.sin(rake);
    const half = len / 2;
    const cx = sx * 0.020 - ux * half;
    const cy = yTop - uy * half;
    const cz = z - uz * half;

    const leg = chamferBox(0.012, len, 0.012, 0.0014);
    rotate(leg, rake, 0, rz);
    translate(leg, cx, cy, cz);
    b.push(leg);

    // Foot at the far end of the same axis.
    const foot = chamferBox(0.020, 0.010, 0.034, 0.0018);
    rotate(foot, rake, 0, 0);
    translate(foot, cx - ux * half, cy - uy * half + 0.004, cz - uz * half);
    b.push(foot);

    // Spring collar just under the yoke.
    b.push(translate(chamferBox(0.016, 0.016, 0.016, 0.0012), sx * 0.020 - ux * 0.020, yTop - uy * 0.020, z - uz * 0.020));
  }
  K.metal(mergeGeom(b));
  return { bottomY: yTop - Math.cos(splay) * Math.cos(rake) * len };
}

/** Laser / illuminator box on a rail — a small asymmetric block with a lens. */
export function railLaser(K, o = {}) {
  const x = o.x ?? -0.028;
  const y = o.y ?? -0.006;
  const z = o.z ?? -0.230;
  K.push(o.bucket ?? 'grey', place(chamferBox(0.024, 0.022, 0.052, 0.0022), x, y, z));
  K.push(o.bucket ?? 'grey', place(chamferBox(0.014, 0.012, 0.012, 0.0016), x, y + 0.016, z + 0.014));
  K.push('accent', translate(discZ(0.0058, z - 0.027, 10, -1), x, y, 0));
  return null;
}

/**
 * Carry handle over the barrel — the belt-fed's tell, and the tightest sight-cone
 * clearance on any weapon that has one. Its top must stay *below* the sight cone; a
 * carry handle that blocks the sight is the same bug as a knuckle that does.
 */
export function carryHandle(K, o = {}) {
  const z = o.z ?? -0.300;
  const len = o.len ?? 0.086;
  const top = o.top ?? 0.0235;
  K.metal(place(chamferBox(0.014, 0.018, 0.016, 0.0016), 0, top - 0.003, z + len / 2));
  K.metal(place(chamferBox(0.014, 0.018, 0.016, 0.0016), 0, top - 0.003, z - len / 2));
  K.metal(place(chamferBox(0.020, 0.011, len, 0.0022), 0, top, z));
  K.rubber(place(chamferBox(0.024, 0.013, len * 0.72, 0.0028), 0, top, z));
  return { top: top + 0.0075 };
}

/* -------------------------------------------------------------------------- */
/* animated parts                                                              */
/* -------------------------------------------------------------------------- */

/** Reciprocating bolt carrier, seen through the ejection port. */
export function boltCarrier(K, o = {}) {
  const b = [];
  b.push(place(chamferBox(o.w ?? 0.028, o.h ?? 0.024, o.len ?? 0.086, 0.0014), o.x ?? 0.004, o.y ?? 0.004, o.z ?? 0.012));
  b.push(place(chamferBox(0.014, 0.016, 0.020, 0.0012), o.x ?? 0.004, o.y ?? 0.004, (o.z ?? 0.012) - (o.len ?? 0.086) * 0.56));
  const m = new THREE.Mesh(mergeGeom(b), K.M.dark);
  m.frustumCulled = false;
  K.parts.bolt = m;
  return m;
}

/** Charging handle. `style` picks where it lives, which is a real silhouette cue. */
export function chargingHandle(K, style, o = {}) {
  const c = [];
  if (style === 'rear') {
    // AR-15: a T-latch out of the back of the upper.
    c.push(place(chamferBox(0.030, 0.008, 0.048, 0.0012), 0, o.y ?? 0.019, o.z ?? 0.086));
    c.push(place(chamferBox(0.062, 0.007, 0.013, 0.0012), 0, o.y ?? 0.019, (o.z ?? 0.086) + 0.020));
    c.push(place(chamferBox(0.014, 0.010, 0.011, 0.0012), -0.030, o.y ?? 0.019, (o.z ?? 0.086) + 0.018));
  } else if (style === 'side') {
    // AK / SCAR: a knob on a slot down the flank.
    const sx = o.side ?? 1;
    c.push(place(chamferBox(0.016, 0.020, 0.040, 0.0014), sx * (o.x ?? 0.030), o.y ?? 0.006, o.z ?? 0.010));
    c.push(place(chamferBox(0.024, 0.013, 0.015, 0.0012), sx * ((o.x ?? 0.030) + 0.009), o.y ?? 0.006, (o.z ?? 0.010) + 0.014));
  } else if (style === 'top') {
    // Uzi / retro subgun: a rib along the top of the receiver.
    c.push(place(chamferBox(0.020, 0.012, 0.056, 0.0014), 0, o.y ?? 0.026, o.z ?? 0.010));
    c.push(place(chamferBox(0.026, 0.014, 0.016, 0.0014), 0, o.y ?? 0.026, (o.z ?? 0.010) + 0.026));
  } else if (style === 'tube') {
    // MP5: a cocking tube standing off the left of the barrel shroud.
    c.push(place(chamferBox(0.013, 0.024, 0.030, 0.0016), -0.026, o.y ?? 0.014, o.z ?? -0.222));
    c.push(place(chamferBox(0.010, 0.010, 0.020, 0.0012), -0.021, o.y ?? 0.014, (o.z ?? -0.222) + 0.010));
  } else {
    return null;
  }
  const m = new THREE.Mesh(mergeGeom(c), K.M.metal);
  m.frustumCulled = false;
  K.parts.charging = m;
  return m;
}

/** Trigger blade. Pivots about its top, which is where the animation layer expects it. */
export function trigger(K, o = {}) {
  const t = new THREE.Mesh(
    mergeGeom([place(chamferBox(o.w ?? 0.0065, o.h ?? 0.026, 0.009, 0.0012), 0, -(o.h ?? 0.026) * 0.42, 0)]),
    K.M.dark
  );
  t.position.set(0, o.y ?? -0.062, o.z ?? 0.040);
  t.frustumCulled = false;
  K.parts.trigger = t;
  return t;
}

/** Wrap a magazine geometry into the detachable group the reload animation drives. */
export function mountMagazine(K, geom, offset, matName = 'poly') {
  const grp = new THREE.Group();
  const m = new THREE.Mesh(geom, K.M[matName]);
  m.frustumCulled = false;
  grp.add(m);
  grp.position.set(offset[0], offset[1], offset[2]);
  K.parts.magazine = grp;
  K.parts.magGeom = geom;
  K.parts.magMat = K.M[matName];
  return grp;
}
